import React, { useCallback, useEffect, useRef, useState } from "react";
import { applyEscapeRoomVideoPlaybackRate } from "./escapeRoomVideoPlayback";

const VIDEO_EXT_RE = /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i;

export function isEscapeRoomVideoUrl(url) {
	if (!url || typeof url !== "string") return false;
	const base = url.split("?")[0].split("#")[0];
	return VIDEO_EXT_RE.test(base);
}

const GRADIENT_PREFIX_RE =
	/^\s*(linear-gradient|radial-gradient|conic-gradient|repeating-linear-gradient|repeating-radial-gradient)\s*\(/i;

export function isEscapeRoomGradientCss(value) {
	if (value == null || typeof value !== "string") return false;
	return GRADIENT_PREFIX_RE.test(value.trim());
}

export function escapeRoomEditorBackgroundSurfaceStyle(colorOrGradient, fallback = "#ffffff") {
	const raw = colorOrGradient != null && String(colorOrGradient).trim() !== "" ? String(colorOrGradient).trim() : "";
	if (!raw) return { backgroundColor: fallback };
	if (isEscapeRoomGradientCss(raw)) {
		return { background: raw, backgroundColor: "transparent" };
	}
	return { backgroundColor: raw };
}

function safeVideoPlay(videoEl) {
	if (!videoEl) return;
	try {
		const p = videoEl.play();
		if (p && typeof p.catch === "function") p.catch(() => {});
	} catch (_) {
		/* autoplay bloqueado o recurso no disponible */
	}
}

export function EscapeRoomBackgroundMediaLayer({
	mediaUrl,
	opacity,
	zIndex = 1,
	borderRadius,
	videoLoop = true,
	onVideoEnded,
	onVideoError,
	videoPlaybackRate = 1,
}) {
	const [mediaFailed, setMediaFailed] = useState(false);
	const rateRef = useRef(videoPlaybackRate);
	rateRef.current = videoPlaybackRate;

	useEffect(() => {
		setMediaFailed(false);
	}, [mediaUrl]);

	const handleMediaError = useCallback(() => {
		setMediaFailed(true);
		if (typeof onVideoError === "function") onVideoError();
	}, [onVideoError]);

	const stallTimerRef = useRef(null);
	const clearStallTimer = useCallback(() => {
		if (stallTimerRef.current) {
			clearTimeout(stallTimerRef.current);
			stallTimerRef.current = null;
		}
	}, []);

	useEffect(() => {
		clearStallTimer();
		if (!mediaUrl || mediaFailed || !isEscapeRoomVideoUrl(mediaUrl)) return undefined;
		if (!onVideoEnded && !onVideoError) return undefined;
		stallTimerRef.current = setTimeout(() => {
			handleMediaError();
		}, 10000);
		return clearStallTimer;
	}, [mediaUrl, mediaFailed, onVideoEnded, onVideoError, handleMediaError, clearStallTimer]);

	const bindVideo = useCallback((node) => {
		if (!node) return;
		applyEscapeRoomVideoPlaybackRate(node, rateRef.current);
		safeVideoPlay(node);
	}, []);

	const handleVideoMetadata = useCallback((e) => {
		clearStallTimer();
		applyEscapeRoomVideoPlaybackRate(e.currentTarget, rateRef.current);
		safeVideoPlay(e.currentTarget);
	}, [clearStallTimer]);

	if (!mediaUrl || mediaFailed) return null;

	const opRaw = opacity != null ? Number(opacity) : 100;
	const op = Number.isFinite(opRaw) ? Math.min(1, Math.max(0, opRaw / 100)) : 1;
	const base = {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex,
		opacity: op,
		pointerEvents: "none",
		overflow: "hidden",
		borderRadius: borderRadius != null ? borderRadius : undefined,
	};

	if (isEscapeRoomVideoUrl(mediaUrl)) {
		return (
			<div style={base}>
				<video
					ref={bindVideo}
					key={mediaUrl}
					src={mediaUrl}
					muted
					playsInline
					autoPlay
					preload="metadata"
					loop={videoLoop !== false}
					style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transform: "translateZ(0)" }}
					onLoadedMetadata={handleVideoMetadata}
					onCanPlay={(e) => {
						clearStallTimer();
						safeVideoPlay(e.currentTarget);
					}}
					onEnded={(e) => {
						clearStallTimer();
						if (typeof onVideoEnded === "function") onVideoEnded(e);
					}}
					onError={() => {
						clearStallTimer();
						handleMediaError();
					}}
				/>
			</div>
		);
	}

	return (
		<div style={base}>
			<img
				src={mediaUrl}
				alt=""
				decoding="async"
				onError={handleMediaError}
				style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
			/>
		</div>
	);
}
